import React from 'react';

interface ReplayControlsProps {
  isActive: boolean;
  isPlaying: boolean;
  speed: number; // ms per candle
  onTogglePlay: () => void;
  onSpeedChange: (newSpeed: number) => void;
  onExit: () => void;
  onStepForward: () => void;
  currentDate?: string;
  isLoadingTicks?: boolean;
  hasTickData?: boolean;
  replayMode?: 'tick' | 'candle';
  onReplayModeChange?: (mode: 'tick' | 'candle') => void;
}

const ReplayControls: React.FC<ReplayControlsProps> = ({
  isActive,
  isPlaying,
  speed,
  onTogglePlay,
  onSpeedChange,
  onExit,
  onStepForward,
  currentDate,
  isLoadingTicks = false,
  hasTickData = true,
  replayMode = 'candle',
  onReplayModeChange
}) => {
  if (!isActive) return null;

  const speedOptions = [1000, 500, 200, 50] as const;
  const currentSpeedIndex = Math.max(0, speedOptions.indexOf(speed as (typeof speedOptions)[number]));
  const nextSpeed = speedOptions[(currentSpeedIndex + 1) % speedOptions.length];
  const speedLabel = speed === 1000 ? '1x' : speed === 500 ? '2x' : speed === 200 ? '5x' : '10x';
  const nextSpeedLabel = nextSpeed === 1000 ? '1x' : nextSpeed === 500 ? '2x' : nextSpeed === 200 ? '5x' : '10x';

  return (
    <div className="absolute top-16 sm:top-20 left-1/2 transform -translate-x-1/2 z-20 flex flex-nowrap items-center gap-1 sm:gap-2 bg-slate-900/90 backdrop-blur-md border border-slate-700 p-1.5 sm:p-2 rounded-xl shadow-2xl animate-fade-in-down max-w-[calc(100vw-1rem)] overflow-hidden">
      
      {/* Exit Button */}
      <button
        onClick={onExit}
        className="p-1.5 sm:p-2 text-rose-400 hover:bg-rose-500/10 rounded-lg transition-colors"
        title="Exit Replay Mode"
      >
        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <div className="hidden sm:block h-6 w-px bg-slate-700 mx-1"></div>

      {/* Jump To / Go To (Visual Placeholder for now) */}
      <div className="hidden sm:block px-2 sm:px-3 py-1 bg-slate-800 rounded text-xs font-mono text-slate-300 min-w-[96px] sm:min-w-[140px] text-center border border-slate-700">
        {currentDate || 'Select Point'}
      </div>

      {/* Replay Mode Toggle */}
      {onReplayModeChange && (
        <>
          <div className="hidden sm:block h-6 w-px bg-slate-700 mx-1"></div>
          <button
            onClick={() => onReplayModeChange(replayMode === 'candle' ? 'tick' : 'candle')}
            className={`sm:hidden p-2 rounded-lg transition-all border ${
              replayMode === 'tick'
                ? 'bg-blue-500/15 border-blue-500/30 text-blue-300'
                : 'bg-slate-800 border-slate-700 text-slate-300'
            }`}
            title={replayMode === 'candle' ? 'Switch to Tick replay' : 'Switch to Candle replay'}
          >
            {replayMode === 'tick' ? (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12h2l2-5 3 10 2-6 2 4h5" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m0 14v2m6-18v2m0 14v2M7 7h4v10H7V7zm6 2h4v6h-4V9z" />
              </svg>
            )}
          </button>

          <div className="hidden sm:flex items-center gap-1 bg-slate-800 rounded-lg p-1 border border-slate-700">
            <button
              onClick={() => onReplayModeChange('candle')}
              className={`px-1.5 sm:px-2 py-1 text-xs font-medium rounded transition-all flex items-center gap-1 ${
                replayMode === 'candle'
                  ? 'bg-blue-500 text-white shadow-lg'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
              title="Candle Replay (Fast, uses candle data)"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m0 14v2m6-18v2m0 14v2M7 7h4v10H7V7zm6 2h4v6h-4V9z" />
              </svg>
              <span className="hidden sm:inline">Candle</span>
            </button>
            <button
              onClick={() => onReplayModeChange('tick')}
              className={`px-1.5 sm:px-2 py-1 text-xs font-medium rounded transition-all flex items-center gap-1 ${
                replayMode === 'tick'
                  ? 'bg-blue-500 text-white shadow-lg'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
              title="Tick Replay (High-res, uses tick data)"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12h2l2-5 3 10 2-6 2 4h5" />
              </svg>
              <span className="hidden sm:inline">Tick</span>
            </button>
          </div>
        </>
      )}

      {/* Loading Indicator */}
      {isLoadingTicks && replayMode === 'tick' && (
        <>
          <div className="sm:hidden flex items-center px-2 py-1 bg-blue-500/10 border border-blue-500/30 rounded text-xs text-blue-400">
            <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <div className="hidden sm:flex items-center gap-2 px-2 py-1 bg-blue-500/10 border border-blue-500/30 rounded text-xs text-blue-400">
            <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Loading ticks...</span>
          </div>
        </>
      )}

      <div className="hidden sm:block h-6 w-px bg-slate-700 mx-1"></div>

      {/* Play/Pause */}
      <button
        onClick={onTogglePlay}
        disabled={(replayMode === 'tick' && (isLoadingTicks || !hasTickData))}
        className={`p-1.5 sm:p-2 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
          (replayMode === 'tick' && (isLoadingTicks || !hasTickData))
            ? 'text-slate-600'
            : isPlaying 
              ? 'text-amber-400 hover:bg-amber-500/10' 
              : 'text-emerald-400 hover:bg-emerald-500/10'
        }`}
        title={
          replayMode === 'tick' && isLoadingTicks 
            ? "Loading tick data..." 
            : replayMode === 'tick' && !hasTickData 
              ? "No tick data available" 
              : isPlaying 
                ? "Pause" 
                : "Play"
        }
      >
        {isPlaying ? (
          <svg className="w-5 h-5 sm:w-6 sm:h-6 fill-current" viewBox="0 0 24 24">
            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
          </svg>
        ) : (
          <svg className="w-5 h-5 sm:w-6 sm:h-6 fill-current" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
      </button>

      {/* Step Forward */}
      <button
        onClick={onStepForward}
        disabled={isPlaying || (replayMode === 'tick' && (isLoadingTicks || !hasTickData))}
        className="p-1.5 sm:p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        title={
          replayMode === 'tick' && isLoadingTicks 
            ? "Loading tick data..." 
            : replayMode === 'tick' && !hasTickData 
              ? "No tick data available" 
              : "Step Forward"
        }
      >
        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
        </svg>
      </button>

      <div className="hidden sm:block h-6 w-px bg-slate-700 mx-1"></div>

      {/* Speed Control */}
      <div className="flex items-center gap-1">
        <button
          onClick={() => onSpeedChange(nextSpeed)}
          className="sm:hidden px-2 py-1 rounded-lg text-xs font-bold transition-colors text-slate-300 hover:bg-slate-800 border border-slate-700 flex items-center gap-1"
          title={`Speed: ${speedLabel} (tap to change to ${nextSpeedLabel})`}
        >
          <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3l2 2m8-2a10 10 0 11-20 0 10 10 0 0120 0z" />
          </svg>
          {speedLabel}
        </button>

        <div className="hidden sm:flex items-center gap-1">
          <svg className="w-4 h-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3l2 2m8-2a10 10 0 11-20 0 10 10 0 0120 0z" />
          </svg>
          <span className="text-[10px] uppercase text-slate-500 font-bold mr-1">Speed</span>
          {speedOptions.map((s) => {
            const label = s === 1000 ? '1x' : s === 500 ? '2x' : s === 200 ? '5x' : '10x';
            return (
              <button
                key={s}
                onClick={() => onSpeedChange(s)}
                className={`px-2 py-1 rounded text-xs font-bold transition-colors ${
                  speed === s
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-500 hover:bg-slate-800 hover:text-slate-300'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ReplayControls;
